// Team Coverage Gauge Component (VAC-8)
// Visual indicator showing % of team on vacation

import React from 'react'
import { AlertCircle, Users } from 'lucide-react'

interface TeamCoverageGaugeProps {
  employeesOnVacation: number
  totalEmployees: number
  employeeNames?: string[]
  className?: string
}

export const TeamCoverageGauge: React.FC<TeamCoverageGaugeProps> = ({
  employeesOnVacation,
  totalEmployees,
  employeeNames = [],
  className = ''
}) => {
  const coveragePercentage = totalEmployees > 0
    ? Math.round((employeesOnVacation / totalEmployees) * 100)
    : 0

  const isWarning = coveragePercentage > 30
  const isCritical = coveragePercentage > 50

  // Determine color based on percentage
  const getColorClass = () => {
    if (isCritical) return 'text-red-600 dark:text-red-400'
    if (isWarning) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-green-600 dark:text-green-400'
  }

  const getBackgroundColorClass = () => {
    if (isCritical) return 'bg-red-500 dark:bg-red-600'
    if (isWarning) return 'bg-yellow-500 dark:bg-yellow-600'
    return 'bg-green-500 dark:bg-green-600'
  }

  const getBorderColorClass = () => {
    if (isCritical) return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
    if (isWarning) return 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
    return 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
  }

  return (
    <div className={`border rounded-lg p-4 ${getBorderColorClass()} ${className}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-lg ${getBackgroundColorClass()} bg-opacity-20`}>
          <Users className={`w-6 h-6 ${getColorClass()}`} />
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900 dark:text-white">
              Cobertura da Equipe
            </h4>
            <span className={`text-2xl font-bold ${getColorClass()}`}>
              {coveragePercentage}%
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
            <div
              className={`h-full ${getBackgroundColorClass()} transition-all duration-300`}
              style={{ width: `${coveragePercentage}%` }}
            />
          </div>

          {/* Stats */}
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            <span className="font-medium">{employeesOnVacation}</span> de{' '}
            <span className="font-medium">{totalEmployees}</span> colaboradores em férias
          </p>

          {/* Warning Message */}
          {isWarning && (
            <div className="flex items-start gap-2 mt-3 pt-3 border-t border-current border-opacity-20">
              <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${getColorClass()}`} />
              <div>
                <p className={`text-sm font-medium ${getColorClass()}`}>
                  {isCritical
                    ? 'Atenção: Cobertura crítica da equipe'
                    : 'Atenção: Alta sobreposição de férias'
                  }
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  {isCritical
                    ? 'Mais da metade da equipe estará em férias neste período.'
                    : 'Verifique se haverá cobertura adequada para as demandas da equipe.'
                  }
                </p>
              </div>
            </div>
          )}

          {/* Employee Names (if provided) */}
          {employeeNames.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Colaboradores em férias:
              </p>
              <div className="flex flex-wrap gap-1">
                {employeeNames.slice(0, 5).map((name, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs text-gray-700 dark:text-gray-300"
                  >
                    {name}
                  </span>
                ))}
                {employeeNames.length > 5 && (
                  <span className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400">
                    +{employeeNames.length - 5} outros
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
